import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Alert, ActivityIndicator, Share, Platform, Modal, TextInput
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { WebView } from 'react-native-webview';
import { useAuth } from '../../contexts/AuthContext';
import SignaturePad from '../../components/SignaturePad';
import * as FileSystem from 'expo-file-system/legacy';
import { generateVoucherHTML } from '../../utils/facturaGenerator';
import api from '../../services/api';
import * as Print from 'expo-print';

const formatCurrency = (val) => `L. ${parseFloat(val || 0).toFixed(2)}`;
const formatFecha = (f) => f ? new Date(f).toLocaleDateString('es-HN', {
  day: '2-digit', month: 'long', year: 'numeric'
}) : '';
const formatFechaCorta = (f) => f ? new Date(f).toLocaleDateString('es-HN', {
  day: '2-digit', month: 'short', year: 'numeric'
}) : '';

const ESTADO_CONFIG = {
  borrador: { color: '#6c757d', bg: '#f8f9fa', icon: 'document-outline', label: 'Borrador' },
  emitido: { color: '#007bff', bg: '#e8f0fe', icon: 'paper-plane-outline', label: 'Emitido' },
  firmado: { color: '#17a2b8', bg: '#d1ecf1', icon: 'finger-print-outline', label: 'Firmado' },
  pagado: { color: '#28a745', bg: '#d4edda', icon: 'checkmark-circle-outline', label: 'Pagado' },
  anulado: { color: '#dc3545', bg: '#f8d7da', icon: 'close-circle-outline', label: 'Anulado' },
};

const DetalleVoucherScreen = ({ route, navigation }) => {
  const { voucherId } = route.params;
  const { user } = useAuth();
  const firmaRef = useRef(null);

  const [voucher, setVoucher] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFirma, setShowFirma] = useState(false);
  const [showVistaPrevia, setShowVistaPrevia] = useState(false);
  const [showAnularModal, setShowAnularModal] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [showFirmaEmisor, setShowFirmaEmisor] = useState(false);
  const [firmando, setFirmando] = useState(false);
  const firmaEmisorRef = useRef(null);

  const isAdmin = ['admin', 'supervisor'].includes(user?.rol);
  const isEmpleado = voucher?.empleado_id === user?.id;

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [voucherData, configData] = await Promise.all([
        api.getVoucher(voucherId),
        api.getConfiguracion().catch(() => ({})),
      ]);
      setVoucher(voucherData);

      // Si no hay logo en la DB, intentar usar el logo local
      if (!configData.logo_url) {
        try {
          const logoPath = FileSystem.documentDirectory + 'logo.png';
          const logoExists = await FileSystem.getInfoAsync(logoPath);
          if (logoExists.exists) {
            const base64 = await FileSystem.readAsStringAsync(logoPath, {
              encoding: FileSystem.EncodingType.Base64,
            });
            configData.logo_url = `data:image/png;base64,${base64}`;
          }
        } catch (logoError) {
          console.log('No se pudo cargar logo local:', logoError);
        }
      }

      setConfig(configData);
    } catch (error) {
      console.error('Error al cargar voucher:', error);
      Alert.alert('Error', 'No se pudo cargar el voucher');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const handleEmitir = useCallback(async (firmaData) => {
    if (!firmaData) {
      return Alert.alert('Error', 'Captura tu firma antes de emitir');
    }
    try {
      await api.emitirVoucher(voucherId, { firma_emisor: firmaData });
      Alert.alert('Voucher Emitido', 'El voucher pasó a estado emitido. Ahora el empleado puede firmarlo.');
      setShowFirmaEmisor(false);
      cargarDatos();
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo emitir el voucher');
    }
  }, [voucherId]);

  const handleFirmarEmpleado = useCallback(async (firmaData) => {
    if (!firmaData) {
      return Alert.alert('Error', 'Dibuja tu firma primero');
    }

    try {
      await api.firmarVoucher(voucherId, { firma_empleado: firmaData });
      Alert.alert('Firmado', 'Voucher firmado exitosamente');
      setShowFirma(false);
      cargarDatos();
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo firmar');
    }
  }, [voucherId]);

  const handlePagar = async () => {
    Alert.alert(
      'Confirmar Pago',
      `¿Marcar voucher ${voucher.numero_voucher} como PAGADO?\nMonto: ${formatCurrency(voucher.monto_neto)}`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar Pago',
          onPress: async () => {
            try {
              await api.pagarVoucher(voucherId);
              Alert.alert('Pagado', 'Voucher marcado como pagado');
              cargarDatos();
            } catch (error) {
              Alert.alert('Error', error.message);
            }
          }
        }
      ]
    );
  };

  const handleAnular = () => {
    setMotivoAnulacion('');
    setShowAnularModal(true);
  };

  const confirmarAnulacion = async () => {
    const motivo = motivoAnulacion.trim() || 'Anulado por administrador';
    setShowAnularModal(false);
    try {
      await api.anularVoucher(voucherId, { motivo });
      Alert.alert('Anulado', 'Voucher anulado exitosamente');
      cargarDatos();
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo anular el voucher');
    }
  };

  const handleImprimir = async () => {
    try {
      const html = generateVoucherHTML(voucher, config);
      await Print.printAsync({ html });
    } catch (error) {
      console.error('Error al imprimir:', error);
      Alert.alert('Error', 'No se pudo imprimir. Verifica la conexión a tu impresora.');
    }
  };

  const handleCompartir = async () => {
    try {
      const html = generateVoucherHTML(voucher, config);
      const { uri } = await Print.printToFileAsync({ html });
      await Share.share({
        url: uri,
        title: `Voucher ${voucher.numero_voucher}`,
        message: `Voucher de pago ${voucher.numero_voucher} - ${formatCurrency(voucher.monto_neto)}`,
      });
    } catch (error) {
      console.error('Error al compartir:', error);
    }
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  if (!voucher) return null;

  const est = ESTADO_CONFIG[voucher.estado] || ESTADO_CONFIG.borrador;
  const ingresos = (voucher.detalles || []).filter(d => d.tipo === 'ingreso');
  const deducciones = (voucher.detalles || []).filter(d => d.tipo === 'deduccion');

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent} scrollEnabled={!firmando}>
      {/* Header con estado */}
      <View style={[styles.headerCard, { borderLeftColor: est.color }]}>
        <View style={styles.headerRow}>
          <View>
            <Text style={styles.voucherNumero}>{voucher.numero_voucher}</Text>
            <Text style={styles.fechaEmision}>Emitido: {formatFecha(voucher.created_at)}</Text>
          </View>
          <View style={[styles.estadoBadge, { backgroundColor: est.bg }]}>
            <Ionicons name={est.icon} size={16} color={est.color} />
            <Text style={[styles.estadoText, { color: est.color }]}>{est.label}</Text>
          </View>
        </View>
      </View>

      {/* Datos del empleado */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>
          <Ionicons name="person-outline" size={15} /> Empleado
        </Text>
        <Text style={styles.empleadoNombre}>{voucher.empleado_nombre}</Text>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Rol:</Text>
          <View style={styles.rolBadge}>
            <Text style={styles.rolBadgeText}>{voucher.empleado_rol}</Text>
          </View>
        </View>
        {voucher.empleado_email && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Email:</Text>
            <Text style={styles.infoValue}>{voucher.empleado_email}</Text>
          </View>
        )}
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Período:</Text>
          <Text style={styles.infoValue}>
            {formatFechaCorta(voucher.periodo_inicio)} - {formatFechaCorta(voucher.periodo_fin)}
          </Text>
        </View>
      </View>

      {/* Desglose de ingresos */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>
          <Ionicons name="trending-up-outline" size={15} color="#28a745" /> Ingresos
        </Text>

        {parseFloat(voucher.salario_base) > 0 && (
          <View style={styles.lineItem}>
            <Ionicons name="add-circle" size={16} color="#28a745" />
            <Text style={styles.lineConcepto}>Salario base</Text>
            <Text style={[styles.lineMonto, { color: '#28a745' }]}>
              {formatCurrency(voucher.salario_base)}
            </Text>
          </View>
        )}

        {parseFloat(voucher.total_comisiones) > 0 && (
          <View style={styles.lineItem}>
            <Ionicons name="add-circle" size={16} color="#28a745" />
            <Text style={styles.lineConcepto}>Comisiones por ventas</Text>
            <Text style={[styles.lineMonto, { color: '#28a745' }]}>
              {formatCurrency(voucher.total_comisiones)}
            </Text>
          </View>
        )}

        {parseFloat(voucher.total_bonos) > 0 && (
          <View style={styles.lineItem}>
            <Ionicons name="add-circle" size={16} color="#28a745" />
            <Text style={styles.lineConcepto}>Bonos</Text>
            <Text style={[styles.lineMonto, { color: '#28a745' }]}>
              {formatCurrency(voucher.total_bonos)}
            </Text>
          </View>
        )}

        {ingresos.map((det, idx) => (
          <View key={`i-${idx}`} style={styles.lineItemDetail}>
            <Text style={styles.lineDetailText}>{det.concepto}</Text>
            <Text style={styles.lineDetailMonto}>{formatCurrency(det.monto)}</Text>
          </View>
        ))}

        <View style={[styles.lineItem, styles.subtotalLine]}>
          <Text style={styles.subtotalLabel}>Total Ingresos</Text>
          <Text style={[styles.subtotalValue, { color: '#28a745' }]}>
            {formatCurrency(voucher.total_ingresos)}
          </Text>
        </View>
      </View>

      {/* Desglose de deducciones */}
      {parseFloat(voucher.total_deducciones) > 0 && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>
            <Ionicons name="trending-down-outline" size={15} color="#dc3545" /> Deducciones
          </Text>

          {parseFloat(voucher.total_penalizaciones) > 0 && (
            <View style={styles.lineItem}>
              <Ionicons name="remove-circle" size={16} color="#dc3545" />
              <Text style={styles.lineConcepto}>Penalizaciones</Text>
              <Text style={[styles.lineMonto, { color: '#dc3545' }]}>
                - {formatCurrency(voucher.total_penalizaciones)}
              </Text>
            </View>
          )}

          {deducciones.map((det, idx) => (
            <View key={`d-${idx}`} style={styles.lineItemDetail}>
              <Text style={styles.lineDetailText}>{det.concepto}</Text>
              <Text style={[styles.lineDetailMonto, { color: '#dc3545' }]}>
                - {formatCurrency(det.monto)}
              </Text>
            </View>
          ))}

          <View style={[styles.lineItem, styles.subtotalLine]}>
            <Text style={styles.subtotalLabel}>Total Deducciones</Text>
            <Text style={[styles.subtotalValue, { color: '#dc3545' }]}>
              - {formatCurrency(voucher.total_deducciones)}
            </Text>
          </View>
        </View>
      )}

      {/* Monto neto */}
      <View style={styles.netoCard}>
        <Text style={styles.netoLabel}>MONTO NETO A PAGAR</Text>
        <Text style={styles.netoValue}>{formatCurrency(voucher.monto_neto)}</Text>
      </View>

      {/* Pago info */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>
          <Ionicons name="wallet-outline" size={15} /> Información de Pago
        </Text>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Método:</Text>
          <Text style={[styles.infoValue, { textTransform: 'capitalize' }]}>
            {voucher.metodo_pago}
          </Text>
        </View>
        {voucher.referencia_pago && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Referencia:</Text>
            <Text style={styles.infoValue}>{voucher.referencia_pago}</Text>
          </View>
        )}
        {voucher.emitido_por_nombre && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Emitido por:</Text>
            <Text style={styles.infoValue}>{voucher.emitido_por_nombre}</Text>
          </View>
        )}
        {voucher.pagado_en && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Pagado:</Text>
            <Text style={styles.infoValue}>{formatFecha(voucher.pagado_en)}</Text>
          </View>
        )}
      </View>

      {/* Firmas */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>
          <Ionicons name="finger-print-outline" size={15} /> Firmas
        </Text>
        <View style={styles.firmasRow}>
          <View style={styles.firmaBox}>
            <Text style={styles.firmaLabel}>Emisor</Text>
            {voucher.firma_emisor ? (
              <View style={styles.firmaImgContainer}>
                <WebView
                  source={{ html: `<img src="${voucher.firma_emisor}" style="width:100%;height:100%;object-fit:contain" />` }}
                  style={styles.firmaImg}
                  scrollEnabled={false}
                />
              </View>
            ) : (
              <View style={styles.firmaVacia}>
                <Text style={styles.firmaVaciaText}>Sin firma</Text>
              </View>
            )}
          </View>
          <View style={styles.firmaBox}>
            <Text style={styles.firmaLabel}>Empleado</Text>
            {voucher.firma_empleado ? (
              <View style={styles.firmaImgContainer}>
                <WebView
                  source={{ html: `<img src="${voucher.firma_empleado}" style="width:100%;height:100%;object-fit:contain" />` }}
                  style={styles.firmaImg}
                  scrollEnabled={false}
                />
              </View>
            ) : (
              <View style={styles.firmaVacia}>
                <Text style={styles.firmaVaciaText}>Sin firma</Text>
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Emitir voucher (admin, borrador) */}
      {voucher.estado === 'borrador' && isAdmin && (
        <View style={styles.card}>
          <TouchableOpacity
            style={styles.firmaToggle}
            onPress={() => setShowFirmaEmisor(!showFirmaEmisor)}
          >
            <Ionicons name="paper-plane-outline" size={20} color="#007bff" />
            <Text style={[styles.firmaToggleText, { color: '#007bff' }]}>
              {showFirmaEmisor ? 'Ocultar firma' : 'Firmar y emitir voucher'}
            </Text>
            <Ionicons name={showFirmaEmisor ? 'chevron-up' : 'chevron-down'} size={18} color="#007bff" />
          </TouchableOpacity>

          {showFirmaEmisor && (
            <View style={{ marginTop: 12 }}>
              <SignaturePad
                ref={firmaEmisorRef}
                onSignature={handleEmitir}
                onDrawingChange={setFirmando}
                height={180}
              />
              <TouchableOpacity
                style={[styles.firmarBtn, { backgroundColor: '#007bff' }]}
                onPress={() => firmaEmisorRef.current?.getSignature()}
              >
                <Ionicons name="checkmark-circle" size={18} color="#fff" />
                <Text style={styles.firmarBtnText}>Confirmar y Emitir</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}

      {/* Firma del empleado (si puede firmar) */}
      {voucher.estado === 'emitido' && (isEmpleado || isAdmin) && !voucher.firma_empleado && (
        <View style={styles.card}>
          <TouchableOpacity
            style={styles.firmaToggle}
            onPress={() => setShowFirma(!showFirma)}
          >
            <Ionicons name="finger-print" size={20} color="#17a2b8" />
            <Text style={styles.firmaToggleText}>Firmar recepción del voucher</Text>
          </TouchableOpacity>

          {showFirma && (
            <View style={{ marginTop: 12 }}>
              <SignaturePad
                ref={firmaRef}
                onSignature={handleFirmarEmpleado}
                onDrawingChange={setFirmando}
                height={180}
              />
              <TouchableOpacity
                style={styles.firmarBtn}
                onPress={() => firmaRef.current?.getSignature()}
              >
                <Ionicons name="checkmark-circle" size={18} color="#fff" />
                <Text style={styles.firmarBtnText}>Confirmar Firma</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}

      {/* Notas */}
      {voucher.notas && (
        <View style={styles.notasCard}>
          <Ionicons name="chatbubble-outline" size={14} color="#856404" />
          <Text style={styles.notasText}>{voucher.notas}</Text>
        </View>
      )}

      {/* Anulación */}
      {voucher.estado === 'anulado' && voucher.motivo_anulacion && (
        <View style={styles.anulacionCard}>
          <Ionicons name="warning-outline" size={16} color="#dc3545" />
          <View style={{ flex: 1, marginLeft: 8 }}>
            <Text style={styles.anulacionTitle}>Voucher Anulado</Text>
            <Text style={styles.anulacionMotivo}>{voucher.motivo_anulacion}</Text>
            {voucher.anulado_por_nombre && (
              <Text style={styles.anulacionPor}>Por: {voucher.anulado_por_nombre}</Text>
            )}
          </View>
        </View>
      )}

      {/* Vista previa / Impresión */}
      {showVistaPrevia && (
        <View style={styles.vistaPreviaContainer}>
          <WebView
            source={{ html: generateVoucherHTML(voucher, config) }}
            style={styles.vistaPrevia}
            scrollEnabled={false}
          />
        </View>
      )}

      {/* Botones de acción */}
      <View style={styles.accionesContainer}>
        <TouchableOpacity
          style={[styles.accionBtn, { backgroundColor: '#6c757d' }]}
          onPress={() => setShowVistaPrevia(!showVistaPrevia)}
        >
          <Ionicons name="eye-outline" size={18} color="#fff" />
          <Text style={styles.accionBtnText}>{showVistaPrevia ? 'Ocultar' : 'Vista Previa'}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.accionBtn, { backgroundColor: '#007bff' }]}
          onPress={handleImprimir}
        >
          <Ionicons name="print-outline" size={18} color="#fff" />
          <Text style={styles.accionBtnText}>Imprimir</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.accionBtn, { backgroundColor: '#17a2b8' }]}
          onPress={handleCompartir}
        >
          <Ionicons name="share-outline" size={18} color="#fff" />
          <Text style={styles.accionBtnText}>Compartir</Text>
        </TouchableOpacity>
      </View>

      {/* Acciones admin */}
      {isAdmin && voucher.estado !== 'anulado' && voucher.estado !== 'pagado' && (
        <View style={styles.adminAcciones}>
          {['emitido', 'firmado'].includes(voucher.estado) && (
            <TouchableOpacity
              style={[styles.accionBtnFull, { backgroundColor: '#28a745' }]}
              onPress={handlePagar}
            >
              <Ionicons name="checkmark-circle-outline" size={20} color="#fff" />
              <Text style={styles.accionBtnFullText}>Marcar como Pagado</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={[styles.accionBtnFull, { backgroundColor: '#dc3545' }]}
            onPress={handleAnular}
          >
            <Ionicons name="close-circle-outline" size={20} color="#fff" />
            <Text style={styles.accionBtnFullText}>Anular Voucher</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Modal para anular voucher */}
      <Modal
        visible={showAnularModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAnularModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Anular Voucher</Text>
            <Text style={styles.modalSubtitle}>Ingresa el motivo de anulación:</Text>
            <TextInput
              style={styles.modalInput}
              value={motivoAnulacion}
              onChangeText={setMotivoAnulacion}
              placeholder="Motivo de anulación..."
              multiline
              autoFocus
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalBtn, styles.modalBtnCancel]}
                onPress={() => setShowAnularModal(false)}
              >
                <Text style={styles.modalBtnCancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalBtn, styles.modalBtnConfirm]}
                onPress={confirmarAnulacion}
              >
                <Text style={styles.modalBtnConfirmText}>Anular</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  scrollContent: { padding: 16, paddingBottom: 40 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  headerCard: {
    backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 12,
    borderLeftWidth: 5, elevation: 2,
  },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  voucherNumero: { fontSize: 20, fontWeight: '700', color: '#333' },
  fechaEmision: { fontSize: 12, color: '#888', marginTop: 2 },
  estadoBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16,
  },
  estadoText: { fontSize: 13, fontWeight: '700' },

  card: {
    backgroundColor: '#fff', borderRadius: 10, padding: 16, marginBottom: 12,
    elevation: 1,
  },
  cardTitle: { fontSize: 14, fontWeight: '700', color: '#333', marginBottom: 12 },

  empleadoNombre: { fontSize: 18, fontWeight: '600', color: '#333', marginBottom: 8 },
  infoRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  infoLabel: { fontSize: 13, color: '#888', width: 80 },
  infoValue: { fontSize: 13, color: '#555', flex: 1 },
  rolBadge: { backgroundColor: '#e8f0fe', paddingHorizontal: 10, paddingVertical: 2, borderRadius: 8 },
  rolBadgeText: { fontSize: 11, color: '#007bff', fontWeight: '600', textTransform: 'uppercase' },

  lineItem: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0',
  },
  lineConcepto: { flex: 1, fontSize: 14, color: '#444' },
  lineMonto: { fontSize: 15, fontWeight: '600' },
  lineItemDetail: {
    flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4,
    paddingLeft: 24,
  },
  lineDetailText: { fontSize: 12, color: '#888', flex: 1 },
  lineDetailMonto: { fontSize: 12, color: '#888' },
  subtotalLine: { borderBottomWidth: 0, borderTopWidth: 1, borderTopColor: '#ddd', marginTop: 4 },
  subtotalLabel: { flex: 1, fontSize: 14, fontWeight: '700', color: '#333' },
  subtotalValue: { fontSize: 16, fontWeight: '700' },

  netoCard: {
    backgroundColor: '#007bff', borderRadius: 12, padding: 20, marginBottom: 12,
    alignItems: 'center', elevation: 3,
  },
  netoLabel: { fontSize: 12, color: 'rgba(255,255,255,0.8)', letterSpacing: 1, fontWeight: '600' },
  netoValue: { fontSize: 28, fontWeight: '700', color: '#fff', marginTop: 4 },

  firmasRow: { flexDirection: 'row', gap: 12 },
  firmaBox: { flex: 1 },
  firmaLabel: { fontSize: 12, color: '#888', marginBottom: 6, textAlign: 'center', fontWeight: '500' },
  firmaImgContainer: { height: 80, borderRadius: 8, borderWidth: 1, borderColor: '#ddd', overflow: 'hidden' },
  firmaImg: { flex: 1 },
  firmaVacia: {
    height: 80, borderRadius: 8, borderWidth: 1, borderColor: '#ddd', borderStyle: 'dashed',
    justifyContent: 'center', alignItems: 'center',
  },
  firmaVaciaText: { fontSize: 12, color: '#ccc' },

  firmaToggle: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  firmaToggleText: { fontSize: 14, color: '#17a2b8', fontWeight: '600' },
  firmarBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    backgroundColor: '#17a2b8', borderRadius: 8, padding: 12, marginTop: 10,
  },
  firmarBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },

  notasCard: {
    flexDirection: 'row', gap: 8, backgroundColor: '#FFF3CD', borderRadius: 8,
    padding: 12, marginBottom: 12,
  },
  notasText: { fontSize: 13, color: '#856404', flex: 1 },

  anulacionCard: {
    flexDirection: 'row', backgroundColor: '#f8d7da', borderRadius: 8,
    padding: 12, marginBottom: 12,
  },
  anulacionTitle: { fontSize: 14, fontWeight: '700', color: '#dc3545' },
  anulacionMotivo: { fontSize: 13, color: '#721c24', marginTop: 2 },
  anulacionPor: { fontSize: 11, color: '#999', marginTop: 4 },

  vistaPreviaContainer: {
    height: 500, borderRadius: 8, overflow: 'hidden', borderWidth: 1,
    borderColor: '#ddd', marginBottom: 12,
  },
  vistaPrevia: { flex: 1 },

  accionesContainer: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  accionBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 4, paddingVertical: 12, borderRadius: 10,
  },
  accionBtnText: { color: '#fff', fontWeight: '600', fontSize: 12 },

  adminAcciones: { gap: 8, marginBottom: 20 },
  accionBtnFull: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingVertical: 14, borderRadius: 10,
  },
  accionBtnFullText: { color: '#fff', fontWeight: '700', fontSize: 15 },

  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff', borderRadius: 12, padding: 20,
    width: '100%', maxWidth: 400, elevation: 10,
  },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#dc3545', marginBottom: 6 },
  modalSubtitle: { fontSize: 14, color: '#666', marginBottom: 12 },
  modalInput: {
    borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 12,
    fontSize: 14, minHeight: 80, textAlignVertical: 'top', backgroundColor: '#fafafa',
  },
  modalButtons: { flexDirection: 'row', gap: 10, marginTop: 16, justifyContent: 'flex-end' },
  modalBtn: { paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  modalBtnCancel: { backgroundColor: '#f0f0f0' },
  modalBtnCancelText: { color: '#666', fontWeight: '600', fontSize: 14 },
  modalBtnConfirm: { backgroundColor: '#dc3545' },
  modalBtnConfirmText: { color: '#fff', fontWeight: '700', fontSize: 14 },
});

export default DetalleVoucherScreen;
